<div class="cpcontent">
    <h1>Virtual Exercise Documentation</h1>
    <h2>Overview</h2>
    Virtual Exercise is an online exercise to be used during remote HAZWOPER refresher training.
    It provides a multi-user experience in a 3D environment.
    The application has the following features:
    <ul>
        <li>Each user has his/her own avatar.</li>
        <li>The application is run within the browser</li>
        <li>No application has to be downloaded and installed</li>
        <li>The exercise has simple controls and minimal learning</li>
    </ul>
    <h2>Technologies used</h2>
    <h5 style="display: inline-block;">Babylon.js</h5>
    <a href="https://www.babylonjs.com/">https://www.babylonjs.com/</a>
    <br>
    The application is run within the browser using HTML, CSS, and Javascript.
    <br><br>
    The 3D immersion is provided with a web rendering engine Babylon.JS
    <a href="https://www.babylonjs.com/">https://www.babylonjs.com/</a>".
    This set of modules provideds the 3D engine, lighting, and camera.
    It uses static models (in .glb format) and animated models the can wlk and idle.
    It provided collision detecion and physics.
    <br>
    <h5 style="display: inline-block;">Firebase Realtime Database</h5>

    <a href="https://firebase.google.com/">https://firebase.google.com/</a>
    <br>
    Google's firebase is a realtime database that is used to store the user's avatar information.
    Each user has a unique ID that is used to store and retrieve the avatar information.
    If a user moves its avatar, the new position is uploaded to the database in realtime.
    If an avatars information is changed in the data, all users are informed of the change in realtime.
    <br><br>
    With this mechanism, every user can see every other user's avatar in realtime.
    <br><br>
    The firebase database is a NoSQL database. The whole database is one JSON object.
    <br><br>
    In our application the database is a json object with the following properties:
    <ul>
        <li><b>Avatars</b>: An array of available avatares and their pictures </li>
        <li>
            <b>Events</b>: Events faciliate messages between users
            The class EventHandler is used to send and receive messages.<br>
            It has the following methods:
            <ul>
                <li><b>Subscribe to Event</b>: every user can subscribe to event by providing a callback function
                    together with a scenarioid and an event type. 
                    The callback function will be called every time an event is created with the specific scenarioid and event type.
                </li>
                <li>
                    <b>Every use can broadcast events</b>. It has to specify the scenarioid and the type.
                    The event contains a "data" object that can very from type to type. It typeically contains a "action" property.
                </li>
                <li><b>Unsubscribe</b>: Not really used</li>
            </ul>

        </li>
        <li>
            "Lessonplans": contains an array of steps that can be used for the teacher to guide through the exercise
        </li>

        <li>
            <b>Scenarios</b>: An array of scenarios that contain:
            <ul>
                <li>The name of the scenario</li>
                <li>The world to be used</li>
                <li>All players with their name, avatar and location</li>
            </ul>

        </li>
        <li><b>Surveys</b>: Multiple Choice questions that can be used throughtout the exercise</li>
        <li>
            <b>Worlds</b>: An object that consists of:
            <ul>
                <li>Name of the world</li>
                <li>An array of components (drums, bottles, houses, etc) with their properties</li>
                <li>Skybox: the background picture at the horizon</li>
                <li>Ground: The ground with texture and terrain</li>
                <li>Walls: the boundary of the game</li>
            </ul>
        </li>
    </ul>
    <h3> Game Mechanics</h3>
    The game is in a repository on github.
    <h5>Main components</h5>
    <ul>
        <li><b>index.html</b>: The main entrance Page. </li>
        <li><b>app.ts</b>: The main typescript program that serves as the entry point.</li>
        <li><b>main-scene.ts</b>: The file initiate the main game engine components.
            <b>beforeRender()</b> is called 60 times per second.
        </li>
        <li><b>world-loader.ts</b>: Called by main-scene to load all components, and interactivities</li>
        <li><b>infoHandler.ts</b>: The InfoHandler class manages the top left information box.
            many classes use it to display information or be called by a button being clicked.
        </li>
        <li><b>wasd.ts</b>: This class manages the keyboard strokes to move characters or do react to other events.</li>
    </ul>
    <h5>Utility Classes</h5>
    <ul>
        <li><b>baseutils</b>: this is a class that any class can be extended to.
        It provides common helper functions</li>
        <li><b>eneventHandler</b>: manages the communication between users. 
            It broadcasts events as entry in the event table in the firebase database.
            Subscribed users will their callback functions be called upon broadcast.
        </li>
        <li><b>firebase-utils</b>Main communication hub between all classes and the firebase database</li>
        <li><b>pointerDownEventHandler</b>: When any user clicks on any mesh in the exercise this catched the evnt.
        It calls callback functions from any interested class</li>
    </ul>
    <h5>Models</h5>
    <ul>
        <li><b>player.ts</b>: Is the definition of all fields of a player in the firebase database.
        Whenever a new character is created this model is appended to the firebase scenario.
        Whenever something is changed like location or position, the model is updatedin the database
        and every user will get the updated player information and can update their local copy. 
        </li>
        <li><b>LocalPlayer</b>: Has all fields and methods that dicribes a player (character).
            
        </li>
        <li><b>World</b>: All elements definig the world
            <ul>
                <li>Components: A list of all meshes and the location</li>
                <li>Skybox</li>
                <li>Walls</li>
                <li>Ground</li>
                <li>Interactivities</li>
            </ul>
        </li>
        <li>Others ....</li>
    </ul>
    <h5>Selected Topics</h5>
    <b>How do characters move?</b><br>
    Every character in the games consists out of a mesh and animations.
    Animations can be walking, idle, jump, or fall.
    <br>
    Main-Scene.beforeRender() is called 60 times per second. 
    It checkes whether a key is pressed and calls the appropriate function, for example
    <b>LocalPlayer.moveForward()</b>.<br>
    In the same method the player properties are updated to the firebase database with <b>this.storePlayerInformation()</b>.
    When the firebase is updated with any player information, the registerd callback function is called, namely:
    <b>Main-scene.handleOtherPlayersUpdates()</b>
    This functions loops over all firebase players and performs the following actions:
    <ul>
        <li>If the firebase player is yourself then ignore it.</li>
        <li>If the player is not in your local list then create the player</li>
        <li>If your local list contains a player that is not in the firebase list, remove the player from your local list</li>
        <li>If the player properties have changed as compared to the last call, update the local player settings.</li>
    </ul>


</div>